import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {renderLanguageCode} from '../../../../../../../libs/shared/core/src/helpers/ai/composeV2Helpers';
import {AiSupportedLanguageCodesEnum} from '../../../../../../../libs/shared/core/src/helpers/ai/constants';
import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {MessageTranslationModel} from '../../../../../../../libs/shared/core/src/models/api/messageTranslationModel';
import {InlineBannerTypesEnum} from '../../../../../../../libs/shared/ui/src/components/banners/inlineBanner/inlineBanner';
import {
  INLINE_BANNER_FIRST_LINE_HEIGHT,
  StyledInlineBannerBase,
  StyledInlineBannerIconDiv,
} from '../../../../../../../libs/shared/ui/src/components/banners/inlineBanner/inlineBannerStyles';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {
  IconButton,
  iconButtonPaddingPx,
} from '../../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {ThreeDots} from '../../../../../../../libs/shared/ui/src/components/composer/threeDots';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {textSelection} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DomMarkdownRenderer} from '../../../../../common/formatters/markdown/domMarkdownRenderer';
import {markdownDomComponents} from '../../../../../common/formatters/markdown/markdownDomComponents';

/*
 * Props.
 */

export interface MessageViewerTranslationProps {
  className?: string;

  detectedLanguage: AiSupportedLanguageCodesEnum;
  translation?: MessageTranslationModel;
  isLoading?: boolean;
  error?: {
    title: ReactNode;
    message: ReactNode;
  };

  renderSettingsDropdown: (popoverProps: PopoverTransferProps) => ReactNode;

  onTranslate: () => void;

  isExpanded: boolean;
  onToggleExpanded: () => void;
  onCancel: () => void;
}

/*
 * Styles.
 */

const StyledTitleDiv = styled.div`
  grid-area: first-line;
  font-size: ${fontSizes.small};
  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.greys.shade90};

  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledContentWrapper = styled.div`
  margin-left: -24px;
  ${textSelection()};
`;

const StyledCollapsibleActionsDiv = styled.div`
  grid-area: close;
  height: ${INLINE_BANNER_FIRST_LINE_HEIGHT}px;
  margin: 0 ${-iconButtonPaddingPx}px 0 ${8 - iconButtonPaddingPx}px;
  gap: 4px;

  display: flex;
  align-items: center;
`;

const StyledContentDiv = styled.div`
  grid-area: more-lines;
  margin-top: 8px;
  font-size: ${fontSizes.verySmall};
  line-height: ${INLINE_BANNER_FIRST_LINE_HEIGHT}px;
`;

/*
 * Component.
 */

export const MessageViewerTranslation: FC<MessageViewerTranslationProps> = ({
  className,
  detectedLanguage,
  translation,
  renderSettingsDropdown,
  isLoading,
  error,
  onTranslate,
  isExpanded,
  onToggleExpanded,
  onCancel,
}) => {
  const theme = useUserTheme();

  function renderTitle() {
    if (error) {
      return error.title;
    }

    if (isLoading) {
      return (
        <>
          <Trans id="message-viewer-translation-banner-title-loading">Translating</Trans>
          <ThreeDots color={theme.palette.blue.shade40} size={VisualSizesEnum.MEDIUM} />
        </>
      );
    }

    if (translation) {
      return (
        <Trans id="message-viewer-translation-banner-title-translated">
          Translated from {renderLanguageCode(detectedLanguage)}
        </Trans>
      );
    }

    return (
      <Trans id="message-viewer-translation-banner-title-untranslated">
        {renderLanguageCode(detectedLanguage)} detected
      </Trans>
    );
  }

  function renderActions(): ReactNode {
    if (isLoading) {
      return [
        <Button
          key="cancel"
          variant={ButtonVariantsEnum.LINK}
          size={VisualSizesEnum.SMALL}
          onClick={onCancel}
        >
          <Trans id="message-viewer-translation-banner-translate-button">Cancel</Trans>
        </Button>,
      ];
    }

    const settingsAction = (
      <PopoverCoordinator
        key="settings"
        renderButton={(isOpen) => (
          <IconButton
            key="settings"
            label={
              <Trans id="message-viewer-translation-banner-settings-button-label">
                Edit translations settings
              </Trans>
            }
            size={VisualSizesEnum.SMALL}
            iconName="gearSmall"
            hasHover={isOpen}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade90}
          />
        )}
        renderModal={renderSettingsDropdown}
      />
    );

    if (translation || error) {
      return (
        <>
          {settingsAction}
          <IconButton
            key="expand-toggle"
            label=""
            size={VisualSizesEnum.SMALL}
            iconName={isExpanded ? 'chevronUpSmall' : 'chevronDownSmall'}
            onClick={onToggleExpanded}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade90}
          />
        </>
      );
    }

    return (
      <>
        <Button
          key="translate"
          variant={ButtonVariantsEnum.LINK}
          size={VisualSizesEnum.SMALL}
          onClick={onTranslate}
        >
          <Trans id="message-viewer-translation-banner-translate-button">Translate</Trans>
        </Button>
        {settingsAction}
      </>
    );
  }

  const renderContent = () => {
    if (error?.message) {
      return <StyledContentWrapper>{error.message}</StyledContentWrapper>;
    }

    if (translation) {
      return (
        <StyledContentWrapper>
          <DomMarkdownRenderer
            debugIdentifier="ai-reply-preview"
            text={translation.translation}
            components={markdownDomComponents}
          />
        </StyledContentWrapper>
      );
    }

    return null;
  };

  return (
    <StyledInlineBannerBase
      className={className}
      $type={error ? InlineBannerTypesEnum.ERROR : InlineBannerTypesEnum.INFO}
    >
      <StyledInlineBannerIconDiv>
        <Icon
          color={error ? theme.palette.red.shade40 : undefined}
          name={error ? 'warningFilledSmall' : 'languageSmall'}
        />
      </StyledInlineBannerIconDiv>
      <StyledTitleDiv>{renderTitle()}</StyledTitleDiv>

      <StyledCollapsibleActionsDiv>{renderActions()}</StyledCollapsibleActionsDiv>

      {isExpanded ? <StyledContentDiv>{renderContent()}</StyledContentDiv> : null}
    </StyledInlineBannerBase>
  );
};
